import { useState, type ReactNode } from 'react';

import {
  Box,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import {
  MENU_GROUPS,
  PROJECTS,
  accentOf,
  destinations,
  projectHref,
  type Destination,
  type Project,
} from '@/designs-dev/loft/projects';
import { CHROME_VH, CHROME_ZOOM } from '@/designs-dev/inspect/chrome';

export type SideMenuProps = {
  onNavigate: (path: string) => void;
  onSignOut: () => void;
  // When provided, a collapse control is shown in the header.
  onCollapse?: () => void;
  // Current location, to mark the active entry.
  currentPath?: string;
};

// A disclosure chevron — right when closed, down when open.
const Chevron = ({ open, size = 9 }: { open: boolean; size?: number }) => (
  <Box sx={{ display: 'flex', color: 'text.primary' }}>
    <Icon
      icon={(open ? 'chevron-down' : 'chevron-right') as never}
      fontSize={size}
    />
  </Box>
);

// The active row reads like a hovered one, and stays that way while the pointer
// is elsewhere. MUI's Mui-selected tint sits too close to the paper to pick out
// of a list.
const activeItemSx = {
  '&.Mui-selected': {
    bgcolor: 'action.hover',
    fontWeight: 600,
    '&:hover': { bgcolor: 'action.hover' },
  },
};

const toggleSx = {
  width: '100%',
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  textAlign: 'left' as const,
  '&:hover': { bgcolor: 'action.hover' },
};

// The Loft menu: the app, then a project per row, each opening a prototype.
//
// Everything that was only navigation is gone: the zone buckets derived from URL
// prefixes, the "Everything in <area>" rows, and the per-project "Overview" row.
// A project with several screens opens into them; the master prototype never
// lists its screens, because walking around inside it is how you get to them.
const SideMenu = ({
  onNavigate,
  onSignOut,
  onCollapse,
  currentPath,
}: SideMenuProps) => {
  const [app, ...projects] = PROJECTS;

  const isInside = (path: string) =>
    currentPath === path || currentPath?.startsWith(`${path}/`);

  // Open the group holding wherever you are, matched against every destination
  // rather than the project's first one: landing on a spec left its project shut,
  // so the menu could not tell you where you were.
  // An archived page opens its project's Archive row as well, and a page in
  // a group opens that group. Groups marked `open` start open regardless.
  const [open, setOpen] = useState<Set<string>>(
    () =>
      new Set(
        projects.flatMap((p) => {
          const always = (MENU_GROUPS[p.slug] ?? [])
            .filter((g) => g.open)
            .map((g) => `${p.slug}/${g.label}`);
          const here = destinations(p).find((d) => isInside(d.path));
          if (!here) return always;
          return [
            ...always,
            p.slug,
            ...(here.archived ? [`${p.slug}/archive`] : []),
            ...(here.group ? [`${p.slug}/${here.group}`] : []),
            ...(here.group && here.concept
              ? [`${p.slug}/${here.group}/concepts`]
              : []),
          ];
        }),
      ),
  );

  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  // Rows sharing a group fold under one heading, drawn where the group's first
  // row falls. Gathered rather than taken in runs, because a group's concepts
  // come from wherever the project lists them, often far from its live rows.
  const runs = (items: Destination[]) => {
    const out: { group?: string; items: Destination[] }[] = [];
    const byGroup = new Map<string, { group?: string; items: Destination[] }>();
    for (const item of items) {
      const existing = item.group ? byGroup.get(item.group) : undefined;
      if (existing) existing.items.push(item);
      else {
        const run = { group: item.group, items: [item] };
        if (item.group) byGroup.set(item.group, run);
        out.push(run);
      }
    }
    return out;
  };

  // One destination means the row is a link. Several means it is a heading that
  // opens: the work is the group, and its screens and specs are ways in. A
  // project that is a folder by intent says so and opens from its first page.
  const renderProject = (project: Project) => {
    const href = projectHref(project);
    const items = destinations(project);

    if (items.length < 2 && !project.folder) {
      return (
        <MenuItem
          key={project.slug}
          selected={isInside(href)}
          disabled={items.length === 0}
          onClick={() => onNavigate(href)}
          sx={activeItemSx}
        >
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                flexShrink: 0,
                borderRadius: '2px',
                bgcolor: accentOf(project),
              }}
            />
            <Typography variant='body2'>{project.title}</Typography>
          </Stack>
        </MenuItem>
      );
    }

    const isOpen = open.has(project.slug);
    const live = items.filter((item) => !item.archived);
    const archived = items.filter((item) => item.archived);
    const archiveKey = `${project.slug}/archive`;
    const archiveOpen = open.has(archiveKey);

    const row = (item: (typeof items)[number], indent: number) => (
      <Box key={item.path}>
        <MenuItem
          // A cross-link never lights up: the screen lives in another
          // project, and that project's row is the one saying where you
          // are. Both lit said the same screen was in two places.
          selected={!item.crossLink && currentPath === item.path}
          onClick={() => onNavigate(item.path)}
          sx={activeItemSx}
        >
          {/* The flows page and whatever leads it are the live work, so
            they carry weight; the rest of the screens sit quieter. A row
            placed under the project's rule carries weight too: it is
            there to group with the spec beside it, not because it has
            been overtaken, and a grey prototype next to a black spec read
            as the prototype being the old one.
            The colour has to go through sx: Typography's `color` prop is
            inert here, so this rule was written and never applied. */}
          <Typography
            sx={{
              pl: indent,
              color:
                (item.isFlows && !item.quiet) || item.leads || item.placed
                  ? 'text.primary'
                  : 'text.secondary',
            }}
            variant='body2'
          >
            {item.label}
          </Typography>
        </MenuItem>
        {/* Closes a group of rows that belong together. */}
        {item.dividerAfter && <Divider sx={{ my: 0.5, ml: 4, mr: 2 }} />}
      </Box>
    );

    return (
      <Box sx={{ pb: 0.5 }} key={project.slug}>
        <Stack
          component='button'
          onClick={() => setOpen((s) => toggle(s, project.slug))}
          direction='row'
          sx={[
            {
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              pt: 1,
              pb: 0.25,
            },
            toggleSx,
          ]}
        >
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                flexShrink: 0,
                borderRadius: '2px',
                bgcolor: accentOf(project),
              }}
            />
            <Typography variant='body2'>{project.title}</Typography>
          </Stack>
          <Chevron open={isOpen} size={8} />
        </Stack>
        <Collapse in={isOpen} unmountOnExit>
          {runs(live).map((run) => {
            if (!run.group) return run.items.map((item) => row(item, 2));
            // A group folds the way Archive does: one row, opening in place
            // one level further in. The last row's rule closes the group.
            const groupKey = `${project.slug}/${run.group}`;
            const groupOpen = open.has(groupKey);
            const work = run.items.filter((item) => !item.concept);
            const concepts = run.items.filter((item) => item.concept);
            const lastWork = work[work.length - 1];
            const conceptsKey = `${groupKey}/concepts`;
            const conceptsOpen = open.has(conceptsKey);
            // The rule under a group, if any of its rows asked for one.
            const closes = run.items.some((item) => item.dividerAfter);
            return (
              <Box key={groupKey}>
                <Stack
                  component='button'
                  onClick={() => setOpen((s) => toggle(s, groupKey))}
                  direction='row'
                  sx={[
                    {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pl: 4,
                      pr: 2,
                      py: 0.75,
                    },
                    toggleSx,
                  ]}
                >
                  <Typography variant='body2'>{run.group}</Typography>
                  <Chevron open={groupOpen} size={8} />
                </Stack>
                <Collapse in={groupOpen} unmountOnExit>
                  {work.map((item) =>
                    row(
                      item === lastWork
                        ? { ...item, dividerAfter: false }
                        : item,
                      4,
                    ),
                  )}
                  {/* A group of nothing but concepts lists them straight
                      away: a Concepts fold would be the group's only row. */}
                  {work.length === 0 &&
                    concepts.map((item) =>
                      row({ ...item, dividerAfter: false }, 6),
                    )}
                  {/* The group's early designs, under a quieter row of their
                      own at its foot, folded until asked for. */}
                  {work.length > 0 && concepts.length > 0 && (
                    <>
                      <Stack
                        component='button'
                        onClick={() => setOpen((s) => toggle(s, conceptsKey))}
                        direction='row'
                        sx={[
                          {
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pl: 6,
                            pr: 2,
                            py: 0.75,
                          },
                          toggleSx,
                        ]}
                      >
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary' }}
                        >
                          Concepts
                        </Typography>
                        <Chevron open={conceptsOpen} size={8} />
                      </Stack>
                      <Collapse in={conceptsOpen} unmountOnExit>
                        {concepts.map((item) =>
                          row({ ...item, dividerAfter: false }, 8),
                        )}
                      </Collapse>
                    </>
                  )}
                </Collapse>
                {closes && <Divider sx={{ my: 0.5, ml: 4, mr: 2 }} />}
              </Box>
            );
          })}
          {/* The history, folded away under one row so the live work is
              what shows. Opens in place, one level further in. */}
          {archived.length > 0 && (
            <>
              <Stack
                component='button'
                onClick={() => setOpen((s) => toggle(s, archiveKey))}
                direction='row'
                sx={[
                  {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: 4,
                    pr: 2,
                    py: 0.75,
                  },
                  toggleSx,
                ]}
              >
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Archive
                </Typography>
                <Chevron open={archiveOpen} size={8} />
              </Stack>
              <Collapse in={archiveOpen} unmountOnExit>
                {archived.map((item) => row(item, 4))}
              </Collapse>
            </>
          )}
        </Collapse>
      </Box>
    );
  };

  return (
    <Paper
      square
      sx={{
        // Docked beside the page from md up. On a phone it would push the page
        // sideways, so it slides over it instead, at most most of the width.
        width: { xs: 'min(280px, 85vw)', md: 280 },
        flexShrink: 0,
        zoom: CHROME_ZOOM,
        height: CHROME_VH(100),
        position: { xs: 'fixed', md: 'sticky' },
        top: 0,
        left: 0,
        zIndex: { xs: 1250, md: 'auto' },
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
        }}
        direction='row'
      >
        <Typography
          component='button'
          variant='h5'
          onClick={() => onNavigate('/')}
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: 0,
          }}
        >
          Loft
        </Typography>
        {onCollapse && (
          <IconButton
            size='small'
            onClick={onCollapse}
            aria-label='Collapse menu'
          >
            <Icon icon='chevrons-left' fontSize={16} />
          </IconButton>
        )}
      </Stack>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {/* The app, above the work and visually apart from it. Its own
            MenuList rather than a bare MenuItem: MUI 9 made the list a
            required context, so an unparented MenuItem throws. Keeping it
            separate from the list below preserves the gap that says the app
            is not one of the projects. */}
        <MenuList dense disablePadding>
          <MenuItem
            selected={isInside('/master')}
            onClick={() => onNavigate(projectHref(app))}
            sx={activeItemSx}
          >
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  flexShrink: 0,
                  borderRadius: '3px',
                  bgcolor: accentOf(app),
                }}
              />
              <Typography variant='body2'>{app.title}</Typography>
            </Stack>
          </MenuItem>
        </MenuList>
        <MenuList dense disablePadding>
          {projects.map(renderProject)}
        </MenuList>
      </Box>
      <Divider />
      <MenuList>
        {/* What Loft is — its own documentation, with a nav of its own. */}
        <MenuItem
          selected={currentPath?.startsWith('/about')}
          onClick={() => onNavigate('/about')}
        >
          About
        </MenuItem>
        {/* The compiled cross-prototype changelog. */}
        <MenuItem
          selected={currentPath === '/changelog'}
          onClick={() => onNavigate('/changelog')}
        >
          Changelog
        </MenuItem>
        <MenuItem onClick={onSignOut}>Sign out</MenuItem>
      </MenuList>
    </Paper>
  );
};

export default SideMenu;
